import React, {useEffect, useRef, useState} from 'react'
import Text from '../../../../components/text/Text'
import WizTag from '../../../../components/tag/WizTag'
import WizSelect from '../../../../components/select/WizSelect'
import DateInput from '../../../../components/input/DateInput'
import {useTime} from '../../../../utils/time'
import UnderLineInput from '../../../../components/input/UnderLineInput'

interface IProps {
    channel?: string
    setChannel?: (channel: string) => void
    visibility?: number
    setVisibility?: (visibility: number) => void
    importantAt?: number
    setImportantAt?: (important_at: number) => void
    pinPriority?: number
    setPinPriority?: (pin_priority: number) => void
    uploadAt?: number
    setUploadAt?: (upload_at: number) => void
    thumbnail?: string
    setThumbnail?: (thumbnail: string) => void
    notify?: boolean
    setNotify?: (notify: boolean) => void
    notifyExpiresAt?: number
    setNotifyExpiresAt?: (notify_expires_at: number) => void
    onClickAddTag?: (category: string) => void
    children?: React.ReactNode
}

const announce_category_list = ['Official', 'Event', 'Media']
const AnnouncementMenuView: React.FC<IProps> = ({
    channel,
    setChannel,
    visibility,
    setVisibility,
    importantAt,
    setImportantAt,
    pinPriority,
    setPinPriority,
    uploadAt,
    setUploadAt,
    thumbnail,
    setThumbnail,
    notify,
    setNotify,
    notifyExpiresAt,
    setNotifyExpiresAt,
    onClickAddTag,
    children,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const dropMenuRef = useRef<HTMLDivElement | null>(null)
    const {convertInputToTimestamp, convertTimestampToInput} = useTime()

    useEffect(() => {
        const handleOutsideClose = (e: {target: any}) => {
            // useRef current에 담긴 엘리먼트 바깥을 클릭 시 드롭메뉴 닫힘
            if (isOpen && !dropMenuRef.current.contains(e.target)) setIsOpen(false)
        }
        document.addEventListener('click', handleOutsideClose)

        return () => document.removeEventListener('click', handleOutsideClose)
    }, [isOpen])

    const onClickExpandTagsDropMenu = () => setIsOpen(prevState => !prevState)

    return (
        <div className={'flex justify-between w-full md:flex-col'}>
            <div className={'w-fit pr-[10px] h-full md:w-full md:pr-0'}>
                <div className="mb-[20px]">{children}</div>
                <div className={'flex items-center gap-x-3 md:flex-col md:items-start md:my-2'}>
                    <div className={'flex gap-x-3'}>
                        <WizSelect
                            optionKey="channel"
                            value={channel}
                            onChange={setChannel}
                            className={'sm:w-full'}
                            buttonClassName={'w-full sm:min-w-[50%] sm:w-full'}
                        />
                        <WizSelect
                            optionKey="visibility"
                            value={visibility}
                            onChange={setVisibility}
                            className={'sm:w-full'}
                            buttonClassName={'w-full sm:min-w-[50%] sm:w-full'}
                        />
                    </div>
                </div>
                <div>
                    <label className={'flex items-center gap-x-2 cursor-pointer mt-5'}>
                        <input
                            type="checkbox"
                            checked={!!notify}
                            onChange={e => setNotify?.(e.target.checked)}
                            className={`w-5 h-5 cursor-pointer appearance-none rounded-[3px] border bg-center bg-no-repeat bg-[length:14px_14px] ${
                                notify ? 'bg-primary border-primary' : 'bg-white border-line02'
                            }`}
                            style={
                                notify
                                    ? {
                                          backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
                                              '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none"><path d="M3 8.5L6.5 12L13 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
                                          )}")`,
                                      }
                                    : undefined
                            }
                        />
                        <Text className={'text-ti2 text-font01'}>Notify (푸시 + 알림센터 + 플로팅)</Text>
                    </label>
                </div>
                <div
                    className={
                        'mt-5 px-[10px] py-[5px] bg-white rounded-[5px] border border-line02 md:mt-[30px] md:w-[100%]'
                    }>
                    <div>
                        <Text className={'text-h3 text-font01'}>Thumbnail Url</Text>
                        <UnderLineInput value={thumbnail} onChanged={setThumbnail} />
                    </div>
                </div>
            </div>

            <div ref={dropMenuRef} className={'w-[300px] pl-[10px] md:w-full md:pl-0 md:mt-[10px] z-[10]'}>
                <div className={'p-[10px] bg-white rounded-[5px] border border-line02 w-full'}>
                    <div>
                        <Text className={'text-body3 text-font02'}>Important At(korea timezone)</Text>
                        <DateInput
                            onChange={e => setImportantAt(convertInputToTimestamp(e.target.value))}
                            value={convertTimestampToInput(importantAt)}
                        />
                    </div>
                    <div className={'mt-[25px]'}>
                        <Text className={'text-body3 text-font02'}>Pin Priority (higher = top, 0 = off)</Text>
                        <input
                            type="number"
                            min={0}
                            className={
                                'w-full mt-[5px] px-[10px] py-[6px] rounded-[5px] border border-line02 text-body3 text-font01 outline-none'
                            }
                            value={pinPriority ?? 0}
                            onChange={e => setPinPriority?.(Number(e.target.value) || 0)}
                        />
                    </div>
                    <div className={'mt-[25px]'}>
                        <Text className={'text-body3 text-font02'}>Upload At(korea timezone)</Text>
                        <DateInput
                            onChange={e => setUploadAt(convertInputToTimestamp(e.target.value))}
                            value={convertTimestampToInput(uploadAt)}
                        />
                    </div>
                    <div className={'mt-[25px]'}>
                        <Text className={'text-body3 text-font02'}>
                            Notify Expires At(korea timezone, 비우면 기본 7일)
                        </Text>
                        <DateInput
                            disabled={!notify}
                            onChange={e => setNotifyExpiresAt?.(convertInputToTimestamp(e.target.value))}
                            value={convertTimestampToInput(notifyExpiresAt)}
                        />
                    </div>
                    <Text
                        className={'mt-[10px] text-h3 text-font01 cursor-pointer'}
                        onClick={onClickExpandTagsDropMenu}>
                        Category
                    </Text>
                    <div
                        className={`${
                            isOpen ? 'opacity-1 py-[10px]' : 'opacity-0 p-0'
                        } transition-all duration-300 ease-in-out `}>
                        {isOpen ? (
                            <div className={'border-t border-line02 p-[5px] space-y-[15px]'}>
                                <div className={'space-y-[5px] mt-[5px]'}>
                                    {announce_category_list.map(item => (
                                        <WizTag
                                            key={item}
                                            textClassName={'bg-icon_bg_black_5 text-font02 font-normal cursor-pointer'}
                                            onClick={() => {
                                                onClickAddTag(item)
                                            }}>
                                            {item}
                                        </WizTag>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    )
}
export default AnnouncementMenuView
